import {
  differenceInMinutes,
  differenceInHours,
  differenceInCalendarDays,
  isValid,
  parseJSON,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// Formatted in a fixed zone (rather than the host's local zone) so the
// output is identical on the server and the client regardless of either
// one's clock/time zone - safe to render during SSR as a placeholder
// before the relative label (which depends on "now" and can differ
// between server and client) is computed client-side after mount.
export const formatReviewDateAbsolute = (reviewedOn: string): string => {
  const reviewed = parseJSON(reviewedOn);
  return isValid(reviewed)
    ? formatInTimeZone(reviewed, 'Australia/Sydney', 'd MMM yyyy')
    : '';
};

export const formatReviewDate = (
  reviewedOn: string,
  now: Date = new Date(),
): string => {
  const reviewed = parseJSON(reviewedOn);

  if (!isValid(reviewed)) {
    return '';
  }

  const minutes = differenceInMinutes(now, reviewed);

  if (minutes < 0) {
    return formatReviewDateAbsolute(reviewedOn);
  }

  if (minutes < 1) {
    return 'Just now';
  }

  if (minutes < 60) {
    return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  }

  const hours = differenceInHours(now, reviewed);
  const days = differenceInCalendarDays(now, reviewed);

  if (hours <= 12 && days === 0) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  if (days === 0) {
    return 'Today';
  }

  if (days === 1) {
    return 'Yesterday';
  }

  if (days >= 2 && days <= 7) {
    return `${days} days ago`;
  }

  return formatReviewDateAbsolute(reviewedOn);
};

export default formatReviewDate;
